import { useEffect, useRef, useState } from 'react'
import { getSharedAudioContext } from '../components/voice/audioContext'

export function useAudioLevel(stream: MediaStream | null): number {
  const [level, setLevel] = useState(0)
  const rafRef = useRef<number>(0)
  const lastLevelRef = useRef(0)
  const streamRef = useRef(stream)
  streamRef.current = stream

  // Use a stable key derived from actual audio track IDs so we don't re-create
  // the analyser every time the MediaStream wrapper object changes reference.
  const trackKey = stream ? stream.getAudioTracks().map((t) => t.id).sort().join(',') : ''

  useEffect(() => {
    const currentStream = streamRef.current
    if (!currentStream || currentStream.getAudioTracks().length === 0) {
      setLevel(0)
      return
    }

    let cancelled = false
    let analyser: AnalyserNode | null = null
    let source: MediaStreamAudioSourceNode | null = null
    // We clone the audio track for analysis so the Web Audio graph and
    // the <audio> element (in LiveKitBackgroundAudio) don't share the
    // same MediaStreamTrack. Chromium has a long-standing bug where a
    // track teed into both an HTMLMediaElement and a
    // MediaStreamAudioSourceNode can end up routed to a single stereo
    // channel after track-lifecycle events (mute/unmute, replaceTrack),
    // making the speaker only audible in one ear. The cloned track
    // shares the same underlying mic source but has an independent
    // lifecycle, which keeps the playback path unaffected.
    let clonedTrack: MediaStreamTrack | null = null

    const start = () => {
      try {
        const ctx = getSharedAudioContext()
        const original = currentStream.getAudioTracks()[0]
        let analysisStream: MediaStream = currentStream
        if (original) {
          clonedTrack = original.clone()
          analysisStream = new MediaStream([clonedTrack])
        }
        source = ctx.createMediaStreamSource(analysisStream)
        analyser = ctx.createAnalyser()
        analyser.fftSize = 256
        analyser.smoothingTimeConstant = 0.3
        source.connect(analyser)

        if (ctx.state === 'suspended') {
          ctx.resume().catch(() => {})
        }

        const floatArray = new Float32Array(analyser.fftSize)

        const tick = () => {
          if (cancelled || !analyser) return

          const s = streamRef.current
          const audioTrack = s?.getAudioTracks()[0]
          if (!audioTrack || !audioTrack.enabled) {
            if (lastLevelRef.current !== 0) {
              lastLevelRef.current = 0
              setLevel(0)
            }
            rafRef.current = requestAnimationFrame(tick)
            return
          }

          analyser.getFloatTimeDomainData(floatArray)

          let sum = 0
          for (let i = 0; i < floatArray.length; i++) {
            sum += floatArray[i] * floatArray[i]
          }
          const rms = Math.sqrt(sum / floatArray.length)

          // Noise gate: ignore anything below a very quiet threshold
          const normalized = rms < 0.008
            ? 0
            : Math.min(100, Math.round(rms * 100 * 5))

          if (Math.abs(normalized - lastLevelRef.current) > 1) {
            lastLevelRef.current = normalized
            setLevel(normalized)
          }
          rafRef.current = requestAnimationFrame(tick)
        }

        rafRef.current = requestAnimationFrame(tick)
      } catch (e) {
        console.warn('AudioMeter setup failed:', e)
      }
    }

    start()

    return () => {
      cancelled = true
      cancelAnimationFrame(rafRef.current)
      if (source) {
        source.disconnect()
        source = null
      }
      if (analyser) {
        analyser.disconnect()
        analyser = null
      }
      if (clonedTrack) {
        try { clonedTrack.stop() } catch { /* noop */ }
        clonedTrack = null
      }
    }
  }, [trackKey])

  return level
}
